// One column's menu — the funnel on its header (Leon, 2026-10-23, mockup v13).
// What it offers depends on the column:
//   choice  (payment, status, hub…)  values with counts
//   date                              Today / This week / This month or From–To
//   money, number                     more than / less than / between
//   text    (refs, IDs)               contains
// With rows selected, a choice column's list works ON THE SELECTION: untick
// Cash and the selected Cash rows are deselected; tick it and its rows join.
// "Filter the table instead" switches the same list to hiding rows. Every
// other change is a draft until Apply.

import { Button } from "@bednbody/ui/components/button";
import { Checkbox } from "@bednbody/ui/components/checkbox";
import { Input } from "@bednbody/ui/components/input";
import { Label } from "@bednbody/ui/components/label";
import { NativeSelect, NativeSelectOption } from "@bednbody/ui/components/native-select";
import { Popover, PopoverContent, PopoverTrigger } from "@bednbody/ui/components/popover";
import { cn } from "@bednbody/ui/lib/utils";
import { ArrowRightIcon, FunnelIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useId, useState } from "react";
import type { SelectionChoice } from "./sheet-selection";
import type { DatePreset, SheetCondition, SortDir, ValueCount } from "./sheet-table.model";
import { PRESET_LABEL } from "./sheet-view";

export type SheetColumnType = "choice" | "date" | "money" | "number" | "text";

export interface MenuSelection {
  /** "7 bookings" — what is selected now. */
  label: string;
  choices: readonly SelectionChoice[];
  onSelect: (values: readonly string[], on: boolean) => void;
  onShowOnly: () => void;
}

export interface SheetColumnMenuProps {
  label: string;
  type: SheetColumnType;
  sort: SortDir | null;
  condition: SheetCondition | null;
  hidden: readonly string[];
  values: readonly ValueCount[];
  /** Null while nothing is selected. */
  selection: MenuSelection | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSort: (dir: SortDir) => void;
  onApply: (filter: { condition: SheetCondition | null; hidden: string[] }) => void;
}

const SORT_LABELS: Record<SheetColumnType, [string, string]> = {
  choice: ["Sort A → Z", "Sort Z → A"],
  text: ["Sort A → Z", "Sort Z → A"],
  date: ["Oldest first", "Newest first"],
  money: ["Lowest first", "Highest first"],
  number: ["Lowest first", "Highest first"],
};

type NumberOp = "none" | "gt" | "lt" | "between";

interface Draft {
  hidden: Set<string>;
  preset: DatePreset | null;
  from: string;
  to: string;
  op: NumberOp;
  a: string;
  b: string;
  text: string;
}

function draftOf(condition: SheetCondition | null, hidden: readonly string[]): Draft {
  const draft: Draft = {
    hidden: new Set(hidden),
    preset: null,
    from: "",
    to: "",
    op: "none",
    a: "",
    b: "",
    text: "",
  };
  if (!condition) return draft;
  if (condition.op === "date")
    return { ...draft, preset: condition.preset, from: condition.from, to: condition.to };
  if (condition.op === "gt" || condition.op === "lt")
    return { ...draft, op: condition.op, a: String(condition.value) };
  if (condition.op === "between")
    return { ...draft, op: "between", a: String(condition.from), b: String(condition.to) };
  if (condition.op === "contains") return { ...draft, text: condition.value };
  return draft;
}

/** The condition a draft describes; `undefined` while its inputs aren't complete. */
function conditionOf(type: SheetColumnType, draft: Draft): SheetCondition | null | undefined {
  if (type === "date") {
    if (draft.preset) return { op: "date", preset: draft.preset, from: "", to: "" };
    return draft.from || draft.to
      ? { op: "date", preset: null, from: draft.from, to: draft.to }
      : null;
  }
  if (type === "money" || type === "number") {
    const isNumber = (value: string) => value.trim() !== "" && Number.isFinite(Number(value));
    if (draft.op === "none") return null;
    if (draft.op === "between") {
      if (!isNumber(draft.a) || !isNumber(draft.b)) return undefined;
      const [x, y] = [Number(draft.a), Number(draft.b)];
      return { op: "between", from: Math.min(x, y), to: Math.max(x, y) };
    }
    return isNumber(draft.a) ? { op: draft.op, value: Number(draft.a) } : undefined;
  }
  if (type === "text")
    return draft.text.trim() ? { op: "contains", value: draft.text.trim() } : null;
  return null;
}

export function SheetColumnMenu({
  label,
  type,
  sort,
  condition,
  hidden,
  values,
  selection,
  open,
  onOpenChange,
  onSort,
  onApply,
}: SheetColumnMenuProps) {
  const id = useId();
  const [search, setSearch] = useState("");
  const [filtering, setFiltering] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftOf(condition, hidden));
  const filtered = hidden.length > 0 || condition !== null;
  const selecting = type === "choice" && selection !== null && !filtering;
  const parsed = conditionOf(type, draft);
  const query = search.trim().toLowerCase();
  const [ascLabel, descLabel] = SORT_LABELS[type];

  const filterValues = values.filter((value) => value.label.toLowerCase().includes(query));
  const selectValues = (selection?.choices ?? []).filter((choice) =>
    choice.label.toLowerCase().includes(query),
  );

  function hideValues(list: readonly string[], hide: boolean) {
    const next = new Set(draft.hidden);
    for (const value of list) {
      if (hide) next.add(value);
      else next.delete(value);
    }
    setDraft({ ...draft, hidden: next });
  }

  function apply() {
    if (parsed === undefined) return;
    onApply({ condition: parsed, hidden: type === "choice" ? [...draft.hidden] : [] });
    onOpenChange(false);
  }

  return (
    <Popover
      onOpenChange={(next) => {
        if (next) {
          setSearch("");
          setFiltering(false);
          setDraft(draftOf(condition, hidden));
        }
        onOpenChange(next);
      }}
      open={open}
    >
      <PopoverTrigger
        render={
          <button
            aria-label={`Filter ${label}${filtered ? " (filtered)" : ""}`}
            className={cn(
              "flex size-6 shrink-0 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              // Only on hover (or while open) unless the column is filtered: a funnel
              // on every header was noise (Leon, 2026-10-26).
              filtered
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground/70 opacity-0 group-hover/head:opacity-100 hover:bg-muted hover:text-foreground focus-visible:opacity-100 data-[popup-open]:opacity-100",
            )}
            type="button"
          />
        }
      >
        <FunnelIcon className="size-3.5" weight={filtered ? "fill" : "regular"} />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 gap-3 p-3 normal-case tracking-normal">
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={() => onSort("asc")}
            size="sm"
            type="button"
            variant={sort === "asc" ? "secondary" : "outline"}
          >
            {ascLabel}
          </Button>
          <Button
            onClick={() => onSort("desc")}
            size="sm"
            type="button"
            variant={sort === "desc" ? "secondary" : "outline"}
          >
            {descLabel}
          </Button>
        </div>

        {selection && !selecting ? (
          <button
            className="flex items-center justify-between rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary hover:bg-primary/15"
            onClick={() => {
              selection.onShowOnly();
              onOpenChange(false);
            }}
            type="button"
          >
            <span>{selection.label}</span>
            <span className="flex items-center gap-1 font-semibold">
              Show only these <ArrowRightIcon className="size-3.5" />
            </span>
          </button>
        ) : null}

        {type === "choice" ? (
          <div className="flex flex-col gap-2 border-t border-border pt-3">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-medium text-foreground">
                {selecting ? "Select by value" : "Show these values"}
              </p>
              <p className="text-xs text-muted-foreground tabular-nums">
                {selecting
                  ? selection?.label
                  : `${values.filter((value) => !draft.hidden.has(value.value)).length} of ${values.length}`}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              {selecting
                ? "Ticks follow your selection. Untick a value to deselect its rows."
                : "Untick a value to hide its rows."}
            </p>
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                aria-label={`Search ${label} values`}
                className="pl-9"
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search values"
                type="search"
                value={search}
              />
            </div>
            <div className="flex gap-3 text-sm">
              <Button
                className="h-auto p-0"
                onClick={() =>
                  selecting
                    ? selection?.onSelect(
                        selectValues.map((choice) => choice.value),
                        true,
                      )
                    : hideValues(
                        filterValues.map((value) => value.value),
                        false,
                      )
                }
                type="button"
                variant="link"
              >
                Select all
              </Button>
              <Button
                className="h-auto p-0"
                onClick={() =>
                  selecting
                    ? selection?.onSelect(
                        selectValues.map((choice) => choice.value),
                        false,
                      )
                    : hideValues(
                        filterValues.map((value) => value.value),
                        true,
                      )
                }
                type="button"
                variant="link"
              >
                Clear
              </Button>
            </div>
            <ul className="flex max-h-56 flex-col overflow-y-auto">
              {(selecting ? selectValues.length : filterValues.length) === 0 ? (
                <li className="px-2 py-1.5 text-sm text-muted-foreground">No values match.</li>
              ) : selecting ? (
                selectValues.map((choice) => (
                  <li key={choice.value}>
                    {/* biome-ignore lint/a11y/noLabelWithoutControl: the Checkbox renders the control */}
                    <label className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-muted">
                      <Checkbox
                        checked={choice.selected > 0}
                        onCheckedChange={() =>
                          selection?.onSelect([choice.value], choice.selected === 0)
                        }
                      />
                      <span className="min-w-0 flex-1 truncate">{choice.label}</span>
                      <span className="font-mono text-xs text-muted-foreground tabular-nums">
                        {choice.selected}/{choice.total}
                      </span>
                    </label>
                  </li>
                ))
              ) : (
                filterValues.map((value) => (
                  <li key={value.value}>
                    {/* biome-ignore lint/a11y/noLabelWithoutControl: the Checkbox renders the control */}
                    <label className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-muted">
                      <Checkbox
                        checked={!draft.hidden.has(value.value)}
                        onCheckedChange={(checked) => hideValues([value.value], !checked)}
                      />
                      <span className="min-w-0 flex-1 truncate">{value.label}</span>
                      <span className="font-mono text-xs text-muted-foreground tabular-nums">
                        {value.count}
                      </span>
                    </label>
                  </li>
                ))
              )}
            </ul>
            {selection ? (
              <Button
                className="h-auto self-start p-0 text-xs"
                onClick={() => setFiltering(!filtering)}
                type="button"
                variant="link"
              >
                {selecting ? "Filter the table instead" : "Back to selecting by value"}
              </Button>
            ) : null}
          </div>
        ) : null}

        {type === "date" ? (
          <div className="flex flex-col gap-2 border-t border-border pt-3">
            <p className="text-sm font-medium text-foreground">Date range</p>
            <div className="grid grid-cols-2 gap-2">
              {([null, "today", "week", "month"] as const).map((preset) => (
                <Button
                  key={preset ?? "all"}
                  onClick={() => setDraft({ ...draft, preset, from: "", to: "" })}
                  size="sm"
                  type="button"
                  variant={
                    draft.preset === preset && !(preset === null && (draft.from || draft.to))
                      ? "default"
                      : "outline"
                  }
                >
                  {preset ? PRESET_LABEL[preset] : "All time"}
                </Button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor={`${id}-from`}>From</Label>
                <Input
                  id={`${id}-from`}
                  onChange={(event) =>
                    setDraft({ ...draft, preset: null, from: event.target.value })
                  }
                  type="date"
                  value={draft.from}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor={`${id}-to`}>To</Label>
                <Input
                  id={`${id}-to`}
                  onChange={(event) => setDraft({ ...draft, preset: null, to: event.target.value })}
                  type="date"
                  value={draft.to}
                />
              </div>
            </div>
          </div>
        ) : null}

        {type === "money" || type === "number" ? (
          <div className="flex flex-col gap-2 border-t border-border pt-3">
            <Label htmlFor={`${id}-op`}>{type === "money" ? "Amount" : "Value"}</Label>
            <NativeSelect
              id={`${id}-op`}
              onChange={(event) =>
                setDraft({ ...draft, op: event.target.value as NumberOp, a: "", b: "" })
              }
              size="sm"
              value={draft.op}
            >
              <NativeSelectOption value="none">
                {type === "money" ? "Any amount" : "Any value"}
              </NativeSelectOption>
              <NativeSelectOption value="gt">More than</NativeSelectOption>
              <NativeSelectOption value="lt">Less than</NativeSelectOption>
              <NativeSelectOption value="between">Between</NativeSelectOption>
            </NativeSelect>
            {draft.op !== "none" ? (
              <div className="flex items-center gap-2">
                <Input
                  aria-label={draft.op === "between" ? "From" : "Amount"}
                  inputMode="decimal"
                  onChange={(event) => setDraft({ ...draft, a: event.target.value })}
                  placeholder={type === "money" ? "₱" : ""}
                  type="number"
                  value={draft.a}
                />
                {draft.op === "between" ? (
                  <Input
                    aria-label="To"
                    inputMode="decimal"
                    onChange={(event) => setDraft({ ...draft, b: event.target.value })}
                    placeholder={type === "money" ? "to ₱" : "to"}
                    type="number"
                    value={draft.b}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {type === "text" ? (
          <div className="flex flex-col gap-2 border-t border-border pt-3">
            <Label htmlFor={`${id}-text`}>Contains</Label>
            <Input
              id={`${id}-text`}
              onChange={(event) => setDraft({ ...draft, text: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === "Enter") apply();
              }}
              placeholder="Type part of it"
              type="text"
              value={draft.text}
            />
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          {selecting ? (
            <Button onClick={() => onOpenChange(false)} size="sm" type="button">
              Done
            </Button>
          ) : (
            <>
              <Button onClick={() => onOpenChange(false)} size="sm" type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={parsed === undefined} onClick={apply} size="sm" type="button">
                Apply
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
